import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { Check, Lock, ArrowRight, ShieldCheck, FileDown, Share2, LayoutGrid, ArrowLeft } from "lucide-react";

const REASON_COPY = {
  save_limit: "You've reached the 2-home free limit — unlock 90 days to save unlimited homes.",
  pdf_export: "PDF exports are part of the 90-day Home Buying Pass.",
  share: "Shareable links are part of the 90-day Home Buying Pass.",
  compare: "Comparing 3 homes side-by-side is part of the 90-day Home Buying Pass.",
  va_residual: "The VA Residual Income Planner is included with the 90-day Pass.",
  va_gap: "The VA Appraisal Gap Calculator is included with the 90-day Pass.",
};

const FEATURES = [
  { icon: LayoutGrid, label: "Unlimited saved homes" },
  { icon: LayoutGrid, label: "Compare up to 3 homes side-by-side" },
  { icon: FileDown, label: "Download branded PDF reports" },
  { icon: Share2, label: "Generate shareable read-only links" },
  { icon: ShieldCheck, label: "Advanced offer scenarios & VA tools (Residual Income, Appraisal Gap)" },
];

export default function Upgrade() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [busy, setBusy] = useState(false);
  const params = new URLSearchParams(loc.search);
  const next = params.get("next") || "/results";
  const reason = params.get("reason");
  const isPremium = user?.entitlement?.plan_type === "premium";

  useEffect(() => {
    if (!loading && !isPremium) {
      track("paywall_view", { reason: reason || undefined, plan_type: "free" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isPremium]);

  const startCheckout = async () => {
    if (!user) {
      // Preserve worksheet in localStorage (context handles that). Route back to /upgrade after login.
      nav(`/login?next=${encodeURIComponent(`/upgrade?next=${encodeURIComponent(next)}`)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        return_path: next,
      });
      track("checkout_started");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const continueFree = () => nav(next);

  if (loading) return null;

  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14 max-w-3xl">
        <button data-testid="upgrade-back" onClick={() => nav(-1)} className="text-sm text-brand-muted hover:text-brand-ink inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> Back
        </button>

        {isPremium ? (
          <div data-testid="already-premium" className="card-hs p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-soft text-brand-accent mb-4"><Check size={26} /></div>
            <h1 className="font-display text-3xl text-brand-ink">You're all set.</h1>
            <p className="text-brand-muted mt-2">Your Home Buying Pass is active — {user.entitlement.days_remaining} days remaining.</p>
            <button onClick={() => nav(next)} className="btn-primary mt-6">Continue <ArrowRight size={16} /></button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <span className="badge-soft" data-testid="upgrade-tag">Home Buying Pass</span>
              <h1 className="font-display text-3xl sm:text-4xl text-brand-ink mt-3">Unlock 90 days — $29.99</h1>
              <p className="text-brand-muted mt-3 max-w-xl mx-auto">One-time payment. Full access to every Home Cost Path tool for 90 days. <b className="text-brand-ink">No automatic renewal.</b></p>
              {reason && REASON_COPY[reason] && (
                <div data-testid="upgrade-reason" className="mt-4 rounded-xl border border-brand-warning/40 bg-brand-warning/10 p-3 text-sm text-brand-ink max-w-xl mx-auto">
                  <Lock size={14} className="inline mr-1 -mt-0.5 text-brand-warning" />
                  {REASON_COPY[reason]}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div data-testid="plan-free" className="card-hs p-6 flex flex-col">
                <div className="text-xs uppercase tracking-wider text-brand-muted">Free</div>
                <div className="font-display text-3xl text-brand-ink mt-2">$0</div>
                <div className="text-xs text-brand-muted">Forever</div>
                <ul className="mt-5 space-y-2.5 text-sm text-brand-ink flex-1">
                  <li className="flex gap-2"><Check size={16} className="text-brand-success mt-0.5 shrink-0" /> Full home-cost analysis (unlimited runs)</li>
                  <li className="flex gap-2"><Check size={16} className="text-brand-success mt-0.5 shrink-0" /> VA funding-fee & closing-cost calculators</li>
                  <li className="flex gap-2"><Check size={16} className="text-brand-success mt-0.5 shrink-0" /> Save up to 2 homes</li>
                  <li className="flex gap-2"><Check size={16} className="text-brand-success mt-0.5 shrink-0" /> Compare 2 homes side-by-side</li>
                  <li className="flex gap-2 text-brand-muted"><Lock size={14} className="mt-1 shrink-0" /> PDF, shareable links, 3-way compare, VA tools</li>
                </ul>
                <button data-testid="continue-free" onClick={continueFree} className="btn-secondary mt-6 w-full">Continue with Free</button>
              </div>

              <div data-testid="plan-pass" className="card-hs p-6 flex flex-col border-brand-accent bg-brand-soft/40 relative">
                <span className="absolute top-4 right-4 text-[10px] uppercase tracking-wider font-semibold text-white bg-brand-accent rounded-full px-2 py-0.5">Recommended</span>
                <div className="text-xs uppercase tracking-wider text-brand-muted">Home Buying Pass</div>
                <div className="font-display text-3xl text-brand-ink mt-2">$29.99</div>
                <div className="text-xs text-brand-muted">One time · 90 days · No auto-renewal</div>
                <ul className="mt-5 space-y-2.5 text-sm text-brand-ink flex-1">
                  {FEATURES.map(f => (
                    <li key={f.label} className="flex gap-2"><Check size={16} className="text-brand-success mt-0.5 shrink-0" /> {f.label}</li>
                  ))}
                </ul>
                <button data-testid="upgrade-checkout" onClick={startCheckout} disabled={busy}
                  className="btn-primary mt-6 w-full">
                  {busy ? "Redirecting to secure checkout…" : <>Unlock 90 Days — $29.99 <ArrowRight size={16} /></>}
                </button>
                <p className="text-[11px] text-brand-muted mt-3 text-center">Secure checkout by Stripe · Test mode</p>
              </div>
            </div>

            <p className="text-xs text-brand-muted mt-8 text-center">
              Home Cost Path is an independent educational planning tool. Estimates are for planning only and are not loan offers or approvals. Access is granted for 90 days and does not automatically renew. See <Link to="/" className="underline">disclaimer</Link>.
            </p>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
